// ════════════════════════════════════════════════════════════
// useAuth — ECR
// ════════════════════════════════════════════════════════════
// Façade du store auth + utilitaires (signIn, signOut).
//
// `bootstrapAuth()` configure le listener Supabase EN PREMIER
// pour que signIn/signOut/refresh notifient toujours le store,
// puis charge la session courante depuis localStorage.
//
// Le store démarre en isLoading=true. Un timeout safety (3s)
// force setLoading(false) si Supabase ne répond pas.
// ════════════════════════════════════════════════════════════
import { useShallow } from 'zustand/react/shallow';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/useAuthStore';
import type { Profile } from '@/types/auth';

const BOOTSTRAP_TIMEOUT_MS = 3000;

interface UseAuthReturn {
  session: ReturnType<typeof useAuthStore.getState>['session'];
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const { session, profile, isLoading } = useAuthStore(
    useShallow((s) => ({
      session: s.session,
      profile: s.profile,
      isLoading: s.isLoading,
    })),
  );

  return {
    session,
    profile,
    isLoading,
    isAuthenticated: Boolean(session),
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error: error?.message ?? null };
    },
    signOut: async () => {
      await supabase.auth.signOut();
      useAuthStore.getState().reset();
    },
  };
}

let bootstrapped = false;

/**
 * À appeler UNE SEULE FOIS au boot de l'app (cf. main.tsx).
 *
 * Ordre crucial :
 *   1. Configure d'abord le listener (SIGNED_IN / SIGNED_OUT / refresh)
 *   2. Charge ensuite la session courante depuis le storage
 *   3. Timeout safety : débloque l'UI au pire après 3s
 */
export async function bootstrapAuth(): Promise<void> {
  if (bootstrapped) return;
  bootstrapped = true;

  const store = useAuthStore.getState();

  // ─── 1. Listener Supabase EN PREMIER ────────────────────
  // Doit être actif avant tout signIn/signOut. Sinon signIn
  // ne notifie pas le store.
  supabase.auth.onAuthStateChange((_event, session) => {
    const s = useAuthStore.getState();
    s.setSession(session);
    s.setLoading(false);

    if (session) {
      void loadProfile(session.user.id);
    } else {
      s.setProfile(null);
    }
  });

  // ─── 2. Safety timeout : débloque l'UI au pire après 3s ─
  const timeoutId = setTimeout(() => {
    if (useAuthStore.getState().isLoading) {
      console.warn(`[Auth] bootstrap timeout (${BOOTSTRAP_TIMEOUT_MS}ms) — débloque l'UI`);
      useAuthStore.getState().setLoading(false);
    }
  }, BOOTSTRAP_TIMEOUT_MS);

  // ─── 3. Charge la session courante ───────────────────────
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn('[Auth] getSession error:', error.message);
    }

    store.setSession(data.session);

    if (data.session) {
      await loadProfile(data.session.user.id);
    }
  } catch (err) {
    console.warn('[Auth] bootstrap error:', err);
  } finally {
    clearTimeout(timeoutId);
    useAuthStore.getState().setLoading(false);
  }
}

async function loadProfile(userId: string): Promise<void> {
  const store = useAuthStore.getState();
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    console.warn('[Auth] loadProfile error:', error.message);
    store.setProfile(null);
    return;
  }

  store.setProfile(data);
}
